import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import AdminLayout from './layouts/AdminLayout';
import Login from './pages/Login';
import Home from './pages/Home';
import Dashboard from './pages/admin/Dashboard';
import Products from './pages/admin/Products';
import Categories from './pages/admin/Categories';
import Orders from './pages/admin/Orders';
import Users from './pages/admin/Users';
import Tables from './pages/admin/Tables';
import Comandas from './pages/admin/Comandas';
import Financeiro from './pages/admin/Financeiro';
import Estoque from './pages/admin/Estoque';
import Plataforma from './pages/Plataforma';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Carregando...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/** Telas administrativas do restaurante: exige papel de administrador.
 *  Fonte da verdade = perfil do vínculo no restaurante ativo (T103);
 *  o operador global (ADMIN) também dirige (equivalência do backend). */
function AdminOnlyRoute({ children }: { children: React.ReactNode }) {
  const { perfilRestaurante } = useAuth();

  if (perfilRestaurante !== 'SUPER_ADMIN') {
    return <Navigate to="/restaurante/orders" replace />;
  }

  return <>{children}</>;
}

/** Área do OPERADOR da plataforma: só o role global ADMIN (dono da plataforma). */
function OperadorRoute({ children }: { children: React.ReactNode }) {
  const { ehOperador } = useAuth();

  if (!ehOperador) {
    return <Navigate to="/restaurante/orders" replace />;
  }

  return <>{children}</>;
}

/**
 * Compat de links antigos: /admin/* era o painel do RESTAURANTE; hoje /admin
 * é o painel do CONTROLADOR, e o painel do restaurante mudou para /restaurante.
 * (Navigate com splat não funciona — por isso o componente com useLocation.)
 */
function RedirecionarAntigoAdmin() {
  const location = useLocation();
  const resto = location.pathname.replace(/^\/admin/, '') || '/';
  return <Navigate to={`/restaurante${resto}`} replace />;
}

export default function App() {
  return (
    <Routes>
      {/* Raiz: landing de venda ou cardápio público (com ?restaurante=/?mesa=) */}
      <Route path="/" element={<Home />} />

      <Route path="/login" element={<Login />} />

      {/* Painel do RESTAURANTE (cliente da plataforma) */}
      <Route
        path="/restaurante"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminOnlyRoute><Dashboard /></AdminOnlyRoute>} />
        <Route path="products" element={<AdminOnlyRoute><Products /></AdminOnlyRoute>} />
        <Route path="categories" element={<AdminOnlyRoute><Categories /></AdminOnlyRoute>} />
        <Route path="orders" element={<Orders />} />
        <Route path="comandas" element={<Comandas />} />
        <Route path="financeiro" element={<AdminOnlyRoute><Financeiro /></AdminOnlyRoute>} />
        <Route path="estoque" element={<AdminOnlyRoute><Estoque /></AdminOnlyRoute>} />
        <Route path="users" element={<AdminOnlyRoute><Users /></AdminOnlyRoute>} />
        <Route path="mesas" element={<AdminOnlyRoute><Tables /></AdminOnlyRoute>} />
      </Route>

      {/* Painel do CONTROLADOR da plataforma (gerencia todos os restaurantes) */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <OperadorRoute>
              <Plataforma />
            </OperadorRoute>
          </ProtectedRoute>
        }
      />

      {/* Compat: endereços antigos */}
      <Route path="/plataforma" element={<Navigate to="/admin" replace />} />
      <Route path="/admin/*" element={<RedirecionarAntigoAdmin />} />
      <Route path="/painel" element={<Navigate to="/restaurante/orders" replace />} />
      <Route path="/cozinha" element={<Navigate to="/restaurante/orders" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}